import generated from '../choices.json' with { type: 'json' };

/**
 * Dataverse choice values, resolved by label.
 *
 * Dataverse derives choice values from the publisher's option value prefix,
 * which it allocates per environment — so the same label can be a different
 * integer in dev and prod. Nothing may hardcode one.
 *
 * `choices.json` is generated by `dataverse/scripts/Export-Choices.ps1` and
 * committed. Until that has been run it is empty, and every lookup here throws
 * with an instruction rather than returning a plausible-looking wrong number.
 */

export interface ExportedColumn {
  /** 'choice' or 'multichoice', as the environment actually has it. */
  type?: string;
  options: Record<string, number>;
}

interface ChoicesFile {
  environment: string | null;
  exportedOn: string | null;
  tables: Record<string, Record<string, ExportedColumn>>;
}

/**
 * Exports before 2026-09-06 stored options directly against the column:
 *
 *   "y365_status": { "Draft": 759280000 }        // old
 *   "y365_status": { "type": "choice",           // new
 *                    "options": { "Draft": 759280000 } }
 *
 * Both are read. An old export's VALUES are still correct, so tolerating it
 * keeps every lookup working and leaves exactly one test failing — the column
 * type check — with a precise instruction. Five cascading failures would bury
 * the one that matters.
 */
function normalise(raw: unknown): ChoicesFile {
  const input = raw as {
    environment: string | null;
    exportedOn: string | null;
    tables?: Record<string, Record<string, unknown>>;
  };

  const tables: Record<string, Record<string, ExportedColumn>> = {};

  for (const [table, columns] of Object.entries(input.tables ?? {})) {
    tables[table] = {};
    for (const [column, value] of Object.entries(columns)) {
      const v = value as Record<string, unknown>;
      if (v && typeof v === 'object' && 'options' in v) {
        tables[table][column] = v as unknown as ExportedColumn;
      } else {
        // Old shape: no type recorded, options at the top level.
        tables[table][column] = { options: v as unknown as Record<string, number> };
      }
    }
  }

  return { environment: input.environment, exportedOn: input.exportedOn, tables };
}

const file = normalise(generated);

export class MissingChoiceError extends Error {
  constructor(table: string, column: string, label: string) {
    super(
      `No Dataverse choice value for ${table}.${column} = "${label}". ` +
        `Run dataverse/scripts/Export-Choices.ps1 against the target environment ` +
        `and commit packages/shared/src/choices.json.`,
    );
    this.name = 'MissingChoiceError';
  }
}

export function choiceValue(table: string, column: string, label: string): number {
  const value = file.tables?.[table]?.[column]?.options?.[label];
  if (typeof value !== 'number') throw new MissingChoiceError(table, column, label);
  return value;
}

/** Reverse lookup, for turning a stored value back into something readable. */
export function choiceLabel(table: string, column: string, value: number): string | undefined {
  const options = file.tables?.[table]?.[column]?.options;
  if (!options) return undefined;
  return Object.keys(options).find((label) => options[label] === value);
}

/** What the ENVIRONMENT has, which may not be what schema.json now says. */
export function exportedColumnType(table: string, column: string): string | undefined {
  return file.tables?.[table]?.[column]?.type;
}

export function choicesExported(): boolean {
  return Object.keys(file.tables ?? {}).length > 0;
}

export const choicesEnvironment = file.environment;

/**
 * When Export-Choices.ps1 last ran, as an ISO string.
 *
 * Worth surfacing in errors. `choices.json` is compiled into the deployment
 * package, so a deployed app carries whatever was exported at BUILD time — and
 * re-exporting locally changes nothing until the API is published again. When a
 * lookup fails, this date is what distinguishes "the export is stale" from "the
 * value is genuinely unknown", and the two have completely different fixes.
 */
export const choicesExportedOn = file.exportedOn;

/** Every label/value pair known for a column, for diagnostics. */
export function choiceOptions(table: string, column: string): Record<string, number> {
  return file.tables?.[table]?.[column]?.options ?? {};
}
